/**
 * Videos on the Pixelmojo YouTube channel (@pixelmojohq).
 *
 * One source for every surface that names a video: the /about embed and its
 * VideoObject, llms.txt and llms-full.txt. Titles, lengths and upload dates
 * are YouTube's own (watch page, checked 2026-09-28). Transcripts are the
 * videos' English captions (product names capitalized, spelling normalized). Summaries and
 * notes restate the video descriptions; never add a claim the video or its
 * description does not make.
 *
 * To add a video: copy its title, lengthSeconds and uploadDate from the watch
 * page, and its transcript from the captions.
 */

import type { VideoItem } from '@/components/seo/AutoSchema'

export interface YouTubeVideo {
  id: string
  title: string
  /** A Short is a vertical cut of the long-form video named in `shortOf`. */
  kind: 'video' | 'short'
  shortOf?: string
  /** YouTube's uploadDate, verbatim. */
  uploadDate: string
  durationSeconds: number
  /** Site page the video belongs with. */
  relatedPath: string
  /** Long-form only. */
  summary?: string
  /** Disclosure carried from the video description. */
  note?: string
  /** Long-form only. */
  transcript?: string
}

export const YOUTUBE_VIDEOS: YouTubeVideo[] = [
  {
    id: 'VJ-rZOCSdnk',
    title: 'Meet Pixelmojo: AI Products for Real Business',
    kind: 'video',
    uploadDate: '2026-09-27T23:14:33-07:00',
    durationSeconds: 65,
    relatedPath: '/about',
    summary:
      'Lloyd Pilapil, founder of Pixelmojo, introduces Radar, Radar Sites, Vector and Hive in one minute.',
    note: 'The introduction uses AI-generated video. The report on screen is an excerpt from the public Radar sample report with client details removed; product scenes are illustrative.',
    transcript:
      "I'm Lloyd, founder of Pixelmojo. We build AI products for discovery, lead qualification, and business operations. Radar is where many teams start. It helps you inspect how AI describes your business, examine the evidence, and decide what to improve next. Your team makes the changes, then checks what happened. We're also developing Radar Sites, a visual builder for websites. Vector qualifies and routes incoming leads around your sales process, giving your team a clearer next step. Hive coordinates specialized AI co-workers so they can share context, hand off work, and bring in people. With Vector and Hive, you own the custom application. We maintain the intelligence. Welcome to Pixelmojo.",
  },
  {
    id: '6VWeG1Xx4rs',
    title: 'Can AI Tell Your Client Apart? A Real AI Visibility Finding',
    kind: 'video',
    uploadDate: '2026-09-27T18:50:32-07:00',
    durationSeconds: 84,
    relatedPath: '/platform/sample-report',
    summary:
      'Scout, the guide character in the Radar videos, walks through one real finding from the public Radar sample report: AI engines describing a different, same-named company. It covers the evidence, who owns each step of the fix, and how to recheck.',
    note: 'Scout scenes are illustrative. The evidence is the de-identified July 2026 baseline from the public sample report; the Gemini example still needs client confirmation, and no follow-up result is published.',
    transcript:
      "AI found your client's name. Great. But is it talking about the right business? I'm Scout. Let's untangle one real Radar finding. This public sample report is a de-identified travel agency's July 2026 baseline. It flagged five identity confusion issues. Three marked high severity. Perplexity mixed in similarly named agencies. Gemini described a company profile that still needs the client's confirmation. Those details matter more than a headline score. Start by confirming the business facts with your client. The official name, location, history, and services. Your content owner makes those details clear on the website. Your web team adds matching organization markup. Then align relevant business listings, that's a practical assignment, with people responsible for it. After the update, check the page and its markup. Repeat comparable AI questions. Record the date, providers, prompts, and responses. Does the confusion remain? No follow-up result is published here. A changed answer doesn't prove your edit caused it. Now, the client gets a clear plan, the evidence, the owner, and the next check. One issue becomes a decision your team can explain and act on. That's Radar, the decision layer for AI visibility. I'm Scout, your guide to Radar.",
  },
  {
    id: 'MXEqaWqyE6c',
    title: 'Does AI Get You? Meet Scout, Your Guide to Radar',
    kind: 'video',
    uploadDate: '2026-09-27T19:02:52-07:00',
    durationSeconds: 20,
    relatedPath: '/platform',
    summary:
      'Scout asks whether AI gets your business, spots a gap in how AI describes a business, and notes what to check next.',
    note: 'The AI-view and next-check graphics are illustrative examples, not a product screenshot or a measured audit result.',
    transcript:
      "Oh, hey. I'm Scout, your guide to Radar. Let's see how AI understands your business and what to check next.",
  },
  {
    id: 'cvb10u2s4w0',
    title: 'When Buyers Ask AI Who to Choose, Do You Make the Shortlist?',
    kind: 'video',
    uploadDate: '2026-09-27T19:45:05-07:00',
    durationSeconds: 18,
    relatedPath: '/platform',
    summary:
      'Buyers ask ChatGPT, Claude, Perplexity and Gemini to compare options on budget, fit and proof. Does your business make the shortlist?',
    transcript:
      "When buyers ask AI who to choose, does your business make the shortlist? Huh? Let's take a closer look.",
  },
  {
    id: 'OVGRKA4WFEY',
    title: 'What Should We Fix First? AI Search Visibility for Agencies',
    kind: 'video',
    uploadDate: '2026-09-28T00:29:45-07:00',
    durationSeconds: 20,
    relatedPath: '/platform',
    summary:
      "Scout shows how an agency can answer a client's question, what should we fix first: inspect how AI describes the business, review the evidence, choose one practical change, then check again.",
    note: 'The AI answer, service page and recheck are an illustrative example, not a real client result.',
    transcript:
      "Your client asks, what should we fix first? I'm Scout. Radar helps you inspect how AI describes their business. Review the evidence. Choose one practical change. Then check again, so your client gets a clear next step.",
  },
  {
    id: 'XBx852xIrmM',
    title: 'Does AI Get Your Business? Meet Scout, Your Guide to Radar #Shorts',
    kind: 'short',
    shortOf: 'MXEqaWqyE6c',
    uploadDate: '2026-09-27T19:06:59-07:00',
    durationSeconds: 20,
    relatedPath: '/platform',
  },
  {
    id: 'cyagQl7pQJ4',
    title:
      'Same Name, Different Business: Can AI Tell Your Client Apart? #Shorts',
    kind: 'short',
    shortOf: '6VWeG1Xx4rs',
    uploadDate: '2026-09-27T19:47:03-07:00',
    durationSeconds: 84,
    relatedPath: '/platform/sample-report',
  },
  {
    id: '_VH6s23xNaM',
    title: 'What Should We Fix First? Radar for Agencies #Shorts',
    kind: 'short',
    shortOf: 'OVGRKA4WFEY',
    uploadDate: '2026-09-28T00:31:31-07:00',
    durationSeconds: 20,
    relatedPath: '/platform',
  },
]

export const youtubeWatchUrl = (id: string) =>
  `https://www.youtube.com/watch?v=${id}`

/** Privacy-mode player. Only this host is allowed in the CSP frame-src. */
export const youtubeEmbedUrl = (id: string) =>
  `https://www.youtube-nocookie.com/embed/${id}`

export const youtubeThumbnailUrl = (id: string) =>
  `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`

/** 84 → "1:24" */
export const formatVideoLength = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`

/** 84 → "PT1M24S" (schema.org duration) */
export const isoVideoDuration = (seconds: number) =>
  `PT${Math.floor(seconds / 60) ? `${Math.floor(seconds / 60)}M` : ''}${seconds % 60 ? `${seconds % 60}S` : ''}`

export function getYouTubeVideo(id: string): YouTubeVideo {
  const video = YOUTUBE_VIDEOS.find(v => v.id === id)
  if (!video) throw new Error(`Unknown YouTube video: ${id}`)
  return video
}

export const LONG_FORM_VIDEOS = YOUTUBE_VIDEOS.filter(v => v.kind === 'video')

export const shortsOf = (id: string) =>
  YOUTUBE_VIDEOS.filter(v => v.shortOf === id)

/** The VideoObject input for AutoSchema's `videos` / `buildVideoSchemas`. */
export const toVideoItem = (v: YouTubeVideo): VideoItem => ({
  name: v.title,
  description: v.summary,
  thumbnailUrl: youtubeThumbnailUrl(v.id),
  uploadDate: v.uploadDate,
  embedUrl: youtubeEmbedUrl(v.id),
  duration: isoVideoDuration(v.durationSeconds),
})
